import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'
import {
    type SubjectType,
    isSubjectTypeUnit,
    type NestedSubtaskElement,
} from '../../../shared/domain/flows/types.no-deps'
import { last, singleton } from '../../../shared/lib/functional/array.no-deps'
import { nilPipe } from '../../../shared/lib/functional/nilPipe.no-deps'
import { field } from '../../../shared/lib/functional/object.no-deps'
import { assertExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import { promiseThen } from '../../../shared/lib/functional/promiseThen.no-deps'

import {
    type KnownUnit,
    type FetchUnitsByIds,
    type FetchUnitsByTypes,
    findRelativesWithUnitType,
    createCachedFetchByIds,
    unitGenerations,
} from '../unit/unit'

export function readyToStart(subjectType: SubjectType, args: { subjectUnitId?: number }) {
    if (subjectType == null) return true

    // If unit subject, then wait until they've picked a unit
    if (isSubjectTypeUnit(subjectType)) return args.subjectUnitId != null

    // If not a unit subject then we do not know how to deal with picking the subject atm
    return assertExhaustion(subjectType)
}

export async function findInferredSubtaskSubjectUnit(
    subtaskElement: NestedSubtaskElement,
    subtaskFlowSubjectType: SubjectType | null,
    parentReportSubjectUnit: KnownUnit | null,
    loopPathIds: LoopPathIds,
    fetchUnitsByIds: FetchUnitsByIds
): Promise<number | null> {
    // If the subtask has no unit subject then bail
    if (subtaskFlowSubjectType == null || !isSubjectTypeUnit(subtaskFlowSubjectType)) {
        return null
    }

    const selectUnitToInferSubject = async () => {
        switch (subtaskElement.subjectUnitInference) {
            case 'loop':
                return nilPipe(loopPathIds, last, field('unitId'), singleton, fetchUnitsByIds, promiseThen(last))
            case 'report':
            case undefined: // Default to report subject if element was created before setting was introduced
                return parentReportSubjectUnit
            default:
                assertExhaustion(subtaskElement.subjectUnitInference)
        }
    }
    const unitToInferSubject = await selectUnitToInferSubject()
    if (!unitToInferSubject) {
        return null
    }

    // If the unit we are inferring from is the same type as child's subject type, use that
    if (unitToInferSubject.type === subtaskFlowSubjectType.unitType) {
        return unitToInferSubject.id
    }

    // If the unit we are inferring from has a singular relative the same type as child's subject type, use that
    const relatives = await findRelativesWithUnitType(
        unitToInferSubject,
        subtaskFlowSubjectType.unitType,
        fetchUnitsByIds
    )
    if (relatives && relatives.length === 1) {
        return relatives[0].id
    }

    return null
}

export interface UnitsForSubjectPicker {
    units: KnownUnit[]
    ancestors: KnownUnit[]
}

export async function fetchUnitsForSubjectPicker(
    subjectType: SubjectType,
    fetchUnitsByType: FetchUnitsByTypes,
    fetchUnitsByIds: FetchUnitsByIds,
    parentSubjectUnitId?: number
): Promise<UnitsForSubjectPicker> {
    if (subjectType == null) return null
    if (!isSubjectTypeUnit(subjectType)) return null

    // If the parent had a unit subject
    if (parentSubjectUnitId != null) {
        const cachedFetchByIds = createCachedFetchByIds(fetchUnitsByIds)

        // Fetch the parents unit subject
        const [parentSubjectUnit] = await cachedFetchByIds([parentSubjectUnitId])

        // Find any relatives if has in the desired unit type
        const relatives = await findRelativesWithUnitType(parentSubjectUnit, subjectType.unitType, cachedFetchByIds)

        // If there are a relatives only show them
        if (relatives && relatives.length > 0) {
            // And use there ancestors in the search tree
            const ancestors = await fetchAncestors(relatives, cachedFetchByIds)
            return { units: relatives, ancestors }
        }
    }

    // Otherwise
    {
        // Find all the units that are in the desired unit type
        const units = await fetchUnitsByType([subjectType.unitType])

        // And get there ancestors for search tree
        const ancestors = await fetchAncestors(units, fetchUnitsByIds)
        return { units, ancestors }
    }
}

export async function fetchAncestors(units: KnownUnit[], fetchByIds: FetchUnitsByIds): Promise<KnownUnit[]> {
    const ancestors: KnownUnit[] = []

    for await (const generation of unitGenerations(units, 'parents', fetchByIds)) {
        // Do not need to categories for seach tree that have no sibilings as that's just extra clicks
        if (generation.length === 1) return ancestors

        // Otherwise add to the array
        ancestors.push(...generation)
    }

    return ancestors
}
